/**
 * CL-EXTID-01 — External ID: the seller's own reference for a listing in another store
 * (a SKU, an eBay/Etsy item number, a stock-sheet row). Seller-only, optional, free text.
 *
 * Stored in the listing's `privateData.externalId` — never `publicData`. publicData is readable by
 * anyone, forever, and Clastique copies it wholesale into every basket order's
 * `protectedData.cart` snapshot (server/api-util/custom/cartListings.js), so a key there would
 * leak to buyers twice over. privateData is returned only to the listing's author, and nothing in
 * this codebase copies it into transaction data.
 *
 * WHY THIS IS CODE AND NOT A CONSOLE LISTING FIELD. The template already handles a
 * `scope: 'private'` listing field end to end (details form → `priv_` namespace →
 * `pickListingFieldsData(…, 'private', …)` → `privateData`; ListingPage reads only publicData and
 * metadata). But this marketplace's listing fields are hosted, and nothing guarantees that the
 * Console copy of this field keeps `scope: private` on every environment — one wrong click on
 * Dev, Test or Live and the value silently starts landing in publicData. Defining it here makes
 * the scope a property of the code: it is the same on every environment from the first deploy,
 * needs no Console promotion step, and is covered by tests.
 *
 * It is written in the HOSTED asset's shape and appended before `restructureListingFields`, so
 * it goes through exactly the same restructuring and validation as a Console field. A hosted
 * field that reuses the key `externalId` is dropped in favour of this one (keys must be unique,
 * and a public copy must never win).
 */

export const EXTERNAL_ID_KEY = 'externalId';

// Hosted listing-fields.json shape (see restructureListingFields in util/configHelpers.js).
export const externalIdListingField = Object.freeze({
  key: EXTERNAL_ID_KEY,
  scope: 'private',
  schemaType: 'shortText',
  label: 'External ID',
  helpText:
    'Your reference for this item in another store (SKU, product ID). Only you can see this.',
  // Every listing type, current and future: the field is useful to any seller.
  listingTypeConfig: { limitToListingTypeIds: false },
  saveConfig: { required: false, placeholderMessage: 'e.g. SKU-10492' },
  // Not searchable: Sharetribe cannot index privateData, and it must never become a filter.
  filterConfig: { indexForSearch: false },
  // Belt and braces: ListingPage never reads privateData, but say so explicitly as well.
  displayOnListingPage: false,
});

/**
 * Add the External ID field to the hosted listing fields, replacing any hosted field that uses
 * the same key.
 *
 * @param {Array<Object>|undefined} hostedListingFields listingFields from listing-fields.json
 * @returns {Array<Object>} hosted fields plus the External ID field (always private scope)
 */
export const withExternalIdListingField = hostedListingFields => {
  const hosted = Array.isArray(hostedListingFields) ? hostedListingFields : [];
  const others = hosted.filter(field => field?.key !== EXTERNAL_ID_KEY);
  if (others.length !== hosted.length) {
    console.warn(
      `Hosted listing field "${EXTERNAL_ID_KEY}" ignored: it is defined in code as a private field (CL-EXTID-01).`
    );
  }
  return [...others, externalIdListingField];
};

/**
 * The seller's External ID from their own listing, or null.
 * Only an own listing carries privateData; for anyone else's this is always null.
 *
 * @param {Object} listing an own listing entity
 * @returns {string|null}
 */
export const getExternalId = listing => {
  const value = listing?.attributes?.privateData?.[EXTERNAL_ID_KEY];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};
